// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Bill of materials runner.
 * @author James Reid
 */

// @ts-check

// @@imports-dependencies
import {
    EpdmCalculator,
    Bom,
    Membrane,
    TrimType,
    // toKebabCase,
    TrimColor,
    MEMBRANES,
    TRIMS,
    Deck,
    Stock,
    // Extra
} from "../../../../package/index.js"

// @@imports-package
import {
    ListSelect,
    QuoteExtrasCard,
    SteppedContainer,
    QuoteConversionPopover,
    QuoteOverviewCard,
    QuoteUpgradeCard
} from "../../components/index.js"

// import { addToBasket } from "./add-to-basket.js"

// @@imports-utils
import {
    apiRequest,
    cloneTemplate,
    fadeElementIn,
    fadeElementOut,
    // fadeElementToggle,
    // getBomEmail,
    // getDeckImageUrl,
    renderLucideIcons
} from "../../utils/index.js"

// @@imports-types
import { State } from "../../types/index.js"
import {
    populateDefaultCard,
    populateMembraneCard,
    // populateExtraCard
} from "./cards/index.js"
import { Layout } from "../Layout.js"
import { App } from "../../app/index.js"
import { getCategorizedBoms } from "./categorized-bom.js"
// import {
//     QuoteConversionPopover,
//     QuoteOverviewCard,
//     QuoteUpgradeCard
// } from "../../components/index.js"

// @@body
const MEMBRANE_OPTIONS = Object
    .values(MEMBRANES)
    .sort(({ priority: a }, { priority: b }) => a - b)
const TRIM_OPTIONS = Object
    .values(TRIMS)
    .sort(({ priority: a }, { priority: b }) => a - b)

const STOCK_CONFIG_OPTIONS = []
for (const membraneOption of MEMBRANE_OPTIONS) {
    for (const trimOption of TRIM_OPTIONS) {
        STOCK_CONFIG_OPTIONS.push({ trimOption, membraneOption })
    }
}

const runBom = async () => {
    const { container, state } = App.instance
    container.unload({ unloadNavs: true })

    state.quote.calculator ??= new EpdmCalculator(
        /** @type {Deck} */ (state.deck.client),
        /** @type {Stock} */ (state.quote.stock),
        { updateConfig: App.instance.state.pageOptions.lifecycle === "new" }
    )
    // apply new client deck to cover case where user returns to start page and
    // starts a new deck/different shape
    state.quote.calculator.deck = state.deck.client
    const epdmCalculator = /** @type {EpdmCalculator} */
        (state.quote.calculator)

    let priceSpan = /** @type {HTMLSpanElement?} */ (null)
    if (!document.body.classList.contains("--hide-prices")) {
        const stepProgressContent = /** @type {HTMLDivElement} */ (App
            .instance.container.progressContainer
            .querySelector("div.step-progress-content"))
        const progressContent = cloneTemplate("bill-of-materials-progress-content")

        priceSpan = /** @type {HTMLSpanElement} */ (progressContent
            .querySelector("span.price"))

        stepProgressContent.append(progressContent)
        fadeElementIn(stepProgressContent)
    }

    try {
        try { epdmCalculator.updateBom() }
        catch {
            let canFulfill = false
            for (const { membraneOption, trimOption } of STOCK_CONFIG_OPTIONS) {
                epdmCalculator.config.trims.type = trimOption.type
                epdmCalculator.config.trims.color = trimOption.color
                epdmCalculator.config.trims.minLength = trimOption.minLength || 0
                epdmCalculator.config.trims.maxLength = trimOption.maxLength || Infinity
                epdmCalculator.config.membrane = membraneOption

                try { epdmCalculator.updateBom() }
                catch { continue }

                canFulfill = true
                break
            }

            if (!canFulfill) { throw new Error("Cannot fulfill quote") }
        }
    }
    catch {
        App.state.pageOptions.lifecycle = "failed"
        App.instance.container.end()

        // apiRequest()

        return
    }
    if (priceSpan) { priceSpan.innerText = epdmCalculator.bom.price.toFixed(2) }

    App.addStateListener(() => {
        try { epdmCalculator.updateBom() }
        catch {
            App.state.pageOptions.lifecycle = "failed"
            App.instance.container.end()

            return
        }

        if (priceSpan) {
            priceSpan.innerText = epdmCalculator.bom.price.toFixed(2)
        }
    })

    const topCardContainer = document.createElement("div")
    topCardContainer.classList.add("card-container")
    topCardContainer.append(
        new QuoteOverviewCard(),
        new QuoteUpgradeCard(),
        new QuoteExtrasCard()
    )

    const content = SteppedContainer.contentContainer()
    content.classList.add("step", "bom")
    content.append(topCardContainer)

    const popover = SteppedContainer.popoverContainer()
    popover.append(new QuoteConversionPopover())
    container.loadPopover(popover)

    container
        .load(content)
        .then(() => container.scrollTo(0, 0))
}

const billOfMaterials = new Layout(runBom, {
    id: "bill-of-materials",
    title: "Review Quote",
    name: "Quote",
})

// @@exports
export { billOfMaterials }
